import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./SubServiceDetail.css";

const serviceData = {
  villa: {
    title: "Villa Construction",
    category: "Residential Construction",
    desc: "We specialize in premium villa construction with modern architecture, structural excellence, and luxury finishing standards — creating dream homes that stand the test of time.",
    features: [
      { icon: "🏗️", title: "Custom Architectural Design", body: "Tailored blueprints matching your vision and lifestyle." },
      { icon: "🪨", title: "Premium Material Selection", body: "Sourcing only the finest materials for lasting quality." },
      { icon: "📋", title: "Structural Engineering Supervision", body: "Expert oversight at every stage of construction." },
      { icon: "⏱️", title: "On-Time Project Delivery", body: "Strict timelines with no compromise on quality." },
    ],
  },
  apartment: {
    title: "Apartment Construction",
    category: "Residential Construction",
    desc: "High-quality multi-unit residential development with full compliance to safety, municipal and structural standards — maximizing value for developers and residents alike.",
    features: [
      { icon: "🏘️", title: "Multi-Unit Planning", body: "Efficient layouts designed for modern living." },
      { icon: "📜", title: "Regulatory Compliance", body: "Full adherence to local municipal building codes." },
      { icon: "📐", title: "Efficient Space Utilization", body: "Every square foot purposefully designed." },
      { icon: "💰", title: "Cost Optimization", body: "Smart strategies that maximize your budget." },
    ],
  },
  renovation: {
    title: "Renovation & Remodeling",
    category: "Residential Construction",
    desc: "Transform existing spaces with structural upgrades, aesthetic improvements, and modern design integration — breathing new life into homes and properties.",
    features: [
      { icon: "🔧", title: "Structural Upgrades", body: "Reinforcing foundations and frameworks for safety." },
      { icon: "✨", title: "Interior Modernization", body: "Updated aesthetics with contemporary finishes." },
      { icon: "🔄", title: "Space Reconfiguration", body: "Reimagining layouts to better serve your needs." },
      { icon: "👷", title: "Complete Project Supervision", body: "End-to-end management from demo to final coat." },
    ],
  },
  office: {
    title: "Office Buildings",
    category: "Commercial Construction",
    desc: "Professional office construction solutions tailored for productivity, design efficiency and corporate branding — building workspaces that inspire performance.",
    features: [
      { icon: "🏢", title: "Corporate Space Planning", body: "Strategic layouts that boost team productivity." },
      { icon: "🪟", title: "Modern Facade Design", body: "Striking exteriors that reinforce brand identity." },
      { icon: "⚡", title: "MEP Integration", body: "Seamless mechanical, electrical and plumbing systems." },
      { icon: "🌱", title: "Sustainable Construction", body: "Eco-friendly practices for a greener footprint." },
    ],
  },
  retail: {
    title: "Retail Spaces",
    category: "Commercial Construction",
    desc: "Retail and commercial space construction designed to enhance customer flow, maximize brand visibility, and create engaging shopping environments.",
    features: [
      { icon: "🛒", title: "Customer-Centric Layout", body: "Intuitive flow design to maximize footfall conversion." },
      { icon: "🖼️", title: "Modern Display Integration", body: "Integrated shelving and display structures." },
      { icon: "🧱", title: "Durable Material Usage", body: "High-traffic finishes built for longevity." },
      { icon: "🚀", title: "Fast-Track Delivery", body: "Accelerated timelines to minimize business downtime." },
    ],
  },
  industrial: {
    title: "Industrial Structures",
    category: "Commercial Construction",
    desc: "Robust industrial building solutions with strict safety compliance, structural reliability, and large-scale execution for warehouses, factories and logistics hubs.",
    features: [
      { icon: "⚙️", title: "Heavy Load Structures", body: "Engineered for maximum weight capacity and durability." },
      { icon: "🔩", title: "Steel Framework Engineering", body: "Precision steel fabrication and assembly." },
      { icon: "🦺", title: "Safety Compliance Systems", body: "Full adherence to industrial safety regulations." },
      { icon: "📏", title: "Large-Scale Execution", body: "Capable of handling projects of any scale." },
    ],
  },
  homeinterior: {
    title: "Home Interior Design",
    category: "Interior Design",
    desc: "Complete interior solutions focused on functionality, comfort, and aesthetic elegance — crafting living spaces that truly feel like home.",
    features: [
      { icon: "🗺️", title: "Space Planning", body: "Optimal furniture arrangement and room flow design." },
      { icon: "🛋️", title: "Custom Furniture Layout", body: "Personalized furniture selection and placement plans." },
      { icon: "💡", title: "Lighting Design", body: "Layered lighting schemes for ambiance and function." },
      { icon: "🎨", title: "Premium Finishing", body: "Curated materials, textures and color palettes." },
    ],
  },
  officeinterior: {
    title: "Office Interior Design",
    category: "Interior Design",
    desc: "Corporate interior design solutions that enhance productivity, reflect brand identity, and create inspiring work environments for modern teams.",
    features: [
      { icon: "💺", title: "Ergonomic Workspace Design", body: "Furniture and layouts that support wellbeing." },
      { icon: "🏷️", title: "Corporate Branding Elements", body: "Integrating brand colors, logos and design language." },
      { icon: "🔇", title: "Acoustic Planning", body: "Sound management for productive, quiet spaces." },
      { icon: "🪵", title: "Modern Material Selection", body: "Premium finishes that balance form and function." },
    ],
  },
  spaceplanning: {
    title: "Space Planning",
    category: "Interior Design",
    desc: "Strategic space utilization to maximize efficiency, functionality and flow — transforming any area into a purposeful, well-organized environment.",
    features: [
      { icon: "🗂️", title: "Functional Zoning", body: "Defining activity zones for clarity and purpose." },
      { icon: "🔀", title: "Flow Optimization", body: "Designing movement paths for maximum convenience." },
      { icon: "📊", title: "Area Utilization Strategy", body: "Making the most of every square foot available." },
      { icon: "🔲", title: "Layout Optimization", body: "Data-driven placement for peak efficiency." },
    ],
  },
  floorplan: {
    title: "Floor Plan",
    category: "Designs",
    desc: "Detailed and accurate 2D floor plans with precise dimensions, room layouts and structural elements — the foundation of every successful build.",
    features: [
      { icon: "📏", title: "Precise Dimensions", body: "Accurate measurements for every room and corridor." },
      { icon: "🚪", title: "Door & Window Placement", body: "Optimized positioning for light and accessibility." },
      { icon: "🔌", title: "Utility Point Marking", body: "Electrical, plumbing and HVAC points clearly indicated." },
      { icon: "📄", title: "Permit-Ready Drawings", body: "Documentation ready for municipal approvals." },
    ],
  },
  layout2d: {
    title: "2D Layout",
    category: "Designs",
    desc: "Precise 2D design layouts for spaces of all sizes and purposes — providing a clear top-down view to plan furniture, fixtures and workflows.",
    features: [
      { icon: "🗺️", title: "Top-Down Visualization", body: "Clear overhead view of the entire space." },
      { icon: "🛠️", title: "Fixture Placement", body: "Accurate positioning of all furniture and fittings." },
      { icon: "📐", title: "Scale Accuracy", body: "True-to-scale drawings for real-world planning." },
      { icon: "🔄", title: "Revision-Friendly Format", body: "Easy to update and iterate during design phases." },
    ],
  },
  elevation3d: {
    title: "3D Elevation",
    category: "Designs",
    desc: "Stunning photorealistic 3D exterior and interior elevation renders — allowing you to visualize your project in full detail before construction begins.",
    features: [
      { icon: "🖼️", title: "Photorealistic Renders", body: "High-quality images showing the finished result." },
      { icon: "🌅", title: "Lighting Simulation", body: "Day and night visualizations with realistic lighting." },
      { icon: "🏠", title: "Exterior Elevations", body: "All four sides rendered with materials and details." },
      { icon: "🎥", title: "Walkthrough Animation", body: "Video tours through your space before construction." },
    ],
  },
};

const SubServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const data = serviceData[slug];

  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: "100px 20px" }}>
        <h2>Service Not Found</h2>
        <button onClick={() => navigate("/services/main")} style={{ marginTop: 20, padding: "12px 28px", cursor: "pointer" }}>
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <div className="ssd-page">
      {/* HERO */}
      <div className="ssd-hero">
        <button className="ssd-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <p className="ssd-category">{data.category}</p>
        <h1>{data.title}</h1>
      </div>

      {/* CONTENT */}
      <div className="ssd-content">
        {/* Description */}
        <div className="ssd-desc-block">
          <p className="ssd-description">{data.desc}</p>
        </div>

        {/* Key Features */}
        <div className="ssd-features-section">
          <h2 className="ssd-features-title">Key Features</h2>
          <div className="ssd-features-grid">
            {data.features.map((f, i) => (
              <div key={i} className="ssd-feature-card">
                <div className="ssd-feature-icon">{f.icon}</div>
                <h4>{f.title}</h4>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="ssd-cta-section">
          <h3>Ready to build with us?</h3>
          <p>Get in touch and our team will guide you through every step of the process.</p>
          <button
            className="ssd-cta-btn"
            onClick={() => navigate("/contact")}
          >
            Start Construction
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubServiceDetail;